import React, { useEffect, useRef, useState } from 'react';
import Flickity from 'react-flickity-component';
import { formatPrice } from '@jbhi-fi/lanyard-ui/global/utils';
import { styles } from './Buttons.css';

interface ButtonsProps {
  name: string;
  variants: Array<{
    imageUrl: string;
    name: string;
    price: number;
    corePrice: number;
    current: boolean;
    handle: string;
    sku: string;
  }>;
  switchProduct: (handle, name) => void;
  itemGapPx?: number;
}

const Buttons = ({ name, variants, switchProduct, itemGapPx = 10 }: ButtonsProps): JSX.Element => {
  const sliderRef = useRef<any>();
  const variantsContainerRef = useRef<any>();
  const [hasSlider, setHasSlider] = useState(variants.length > 4);
  const [maxItemsInView, setMaxItemsInView] = useState(0);
  const initialSlide = variants?.findIndex(variant => !!variant.current);
  const formattedIdName = `button-variants-${name.toLowerCase().replace(/\s/g, '-')}`;

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      // Checking if the total width of all options is bigger than the width of the container, if yes, we initialize the slider
      if (variantsContainerRef.current) {
        setTimeout(() => {
          const sliderElements = variantsContainerRef.current.querySelectorAll('.flickity-slider a');
          const sliderElementCount = sliderElements.length;
          if (sliderElements && sliderElementCount) {
            const containerWidth = variantsContainerRef.current.offsetWidth;
            let totalWidth = 0;
            sliderElements.forEach(
              (element, index) =>
                (totalWidth += element.offsetWidth + (index + 1 !== sliderElementCount ? itemGapPx : 0))
            );
            if (totalWidth > containerWidth) {
              // Checking to see how many items can actually fit in our viewport at one time as viewport is different to container width
              let totalViewWidth = 0;
              const viewPortWidth = variantsContainerRef.current.querySelector('.flickity-viewport').offsetWidth;
              for (let index = 0; index < sliderElementCount; index++) {
                totalViewWidth +=
                  sliderElements[index].offsetWidth + (index + 1 !== sliderElementCount ? itemGapPx : 0);
                if (totalViewWidth > viewPortWidth) {
                  setMaxItemsInView(index);
                  break;
                }
              }
              setHasSlider(true);
            } else {
              setHasSlider(false);
            }
          }
        }, 300);
      }
    }

    return () => {
      mounted = false;
    };
  }, [variants]);

  const getCurrentSlide = () =>
    sliderRef.current &&
    (Math.floor(initialSlide / maxItemsInView) >= sliderRef.current.slides.length
      ? sliderRef.current.slides.length - 1
      : Math.floor(initialSlide / maxItemsInView));

  return (
    <div className={styles.variant} id={formattedIdName}>
      <div className={styles.title}>{name}</div>
      <div
        ref={ref => (variantsContainerRef.current = ref)}
        className={styles.container}
        data-slider-enabled={hasSlider}
      >
        <Flickity
          key={Date.now()}
          elementType={'div'}
          flickityRef={ref => (sliderRef.current = ref)}
          disableImagesLoaded={false}
          options={{
            groupCells: true,
            prevNextButtons: hasSlider,
            pageDots: false,
            freeScroll: true,
            wrapAround: false,
            contain: true,
            cellAlign: 'left',
            draggable: hasSlider,
            percentPosition: false,
            initialIndex: hasSlider ? getCurrentSlide() : initialSlide,
          }}
        >
          {variants.map(variant => (
            <a
              key={`${variant.sku}-${variant.handle}`}
              href={`/products/${variant.handle}`}
              onClick={e => {
                if (e.metaKey || e.ctrlKey) return; // Allow opening in new tab with cmd/ctrl + click
                e.preventDefault();
                switchProduct(variant?.handle, name);
              }}
              className={!!variant.current ? 'current' : ''}
              data-price={variant.price}
              data-core-price={variant.corePrice}
              data-test-id="variant-button"
            >
              <span>{variant[name]?.value}</span>${formatPrice(variant?.price)}
            </a>
          ))}
        </Flickity>
      </div>
    </div>
  );
};

export default Buttons;
